import React, { useEffect } from "react";
import { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

import ActionButtons from "./ActionButtons";
import logo from "../../images/logo.png";
import agentAvatar from "../../images/logo.png"; // Adjust if you have a separate agent avatar
import Attachment from "./Attachment";
import getValueOrUndefined from "../../util/getValueOrUndefined";
import SuggestionActionList from "./SuggestedActionList";
import { BiSupport } from "react-icons/bi";
import SourceList from "./SourceList";
import PersonIcon from "../../images/personicon.svg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FaRegCopy, FaCheck } from "react-icons/fa";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "../ui/tooltip";
import Offers from "./Offers";
import OfferDetail from "./OfferDetail";
import PendingActionCards from "./PendingActionCards";
import PaymentReceiptCard from "./PaymentReceiptCard";
import aedRegularFont from '../../assets/aed-Regular.otf';

// Add the font CSS styles with both Azure and local fallback
const fontStyles = `
 @font-face {
  font-family: 'AED-Regular';
  src: url('./assets/aed-Regular.otf') format('opentype');
  font-weight: normal;
  font-style: normal;
  font-display: swap;
}
  
  .dirham-symbol {
    font-family: 'AED-Regular', 'Arial', sans-serif !important;
    font-weight: normal;
  }
`;

const BotThinking = ({ visible, message = "Rammas is thinking", isArabic }) => {
  const localizedMessage = isArabic ? "رماس يفكر" : message;

  const keyframes = `
    @keyframes wave {
      0%, 20% { opacity: 0.3; }
      50% { opacity: 1; }
      100% { opacity: 0.3; }
    }
  `;

  const textAndDotStyle = {
    display: "inline-block",
    color: "#007560",
    fontSize: "18px",
    fontWeight: "500",
    animationName: "wave",
    animationDuration: "2s",
    animationIterationCount: "infinite",
    animationTimingFunction: "ease-in-out",
    opacity: visible ? 1 : 0,
    transition: "opacity 1s ease-in-out",
  };

  const textArray = ["●", "●", "●"];

  const containerStyle = {
    direction: isArabic ? "rtl" : "ltr",
  };

  const messageStyle = {
    marginRight: isArabic ? "0" : "8px",
    marginLeft: isArabic ? "8px" : "0",
    opacity: visible ? 1 : 0,
    transition: "opacity 1s ease-in-out",
  };

  const dotStyle = (index) => ({
    ...textAndDotStyle,
    animationDelay: `${index * 0.1}s`,
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#000000",
    margin: "0 2px",
  });

  return (
    <div className="flex items-center" style={containerStyle}>
      <style>{keyframes}</style>
      <span style={{ display: "inline-flex", alignItems: "center" }}>
        <span style={messageStyle}>{localizedMessage}</span>
        {textArray.map((char, index) => (
          <span key={index} style={dotStyle(index)}>
            {char !== "●" ? (char === " " ? "\u00A0" : char) : ""}
          </span>
        ))}
      </span>
    </div>
  );
};

/**
 * Wrap phone numbers in an LTR span so the browser Bidi algorithm cannot
 * reorder their digits when they appear inside an RTL paragraph.
 * Using dir="ltr" on a <span> is the only reliable cross-browser fix —
 * Unicode directional marks (LRM / LRI) are overridden by RTL paragraphs
 * in some rendering engines and are also invisible to assistive technology.
 *
 * Patterns covered:
 *   (04) 601 9999   04 601 9999   050 123 4567
 *   +971 4 601 9999               800 4339
 */
const fixPhoneNumbersRTL = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(
    /(\(0\d{1,2}\)\s*[\d\s\-]{5,}|0[2-9]\d?\s[\d][\d\s\-]{5,}|\+971[\s\-]?\d{1,2}[\s\-]?\d{3,4}[\s\-]?\d{4}|\b800[\s\-]\d{4,})/g,
    (match) => `<span dir="ltr" style="unicode-bidi:isolate">${match.trim()}</span>`
  );
};

// Component to handle markdown rendering and AED replacement
const MarkdownWithAEDReplacement = ({ children, ...markdownProps }) => {
  const [processedHTML, setProcessedHTML] = useState('');
  const [fontLoaded, setFontLoaded] = useState(false);
  const [fontError, setFontError] = useState('');
  const containerRef = React.useRef(null);

  // Check if font is loaded
  React.useEffect(() => {
  const checkFont = async () => {
    try {
      if ('fonts' in document) {
        let fontLoaded = false;

        try {
          // 1. Try Azure hosted font (URL from App Service env var via window.contextvars)
          const blobStorageUrl = (typeof window !== 'undefined' && window.contextvars && window.contextvars.REACT_APP_BLOB_STORAGE_URL) || '';
          const azureFontUrl = blobStorageUrl ? `${blobStorageUrl.replace(/\/+$/, '')}/fonts/aed-Regular.otf` : null;
          if (!azureFontUrl) throw new Error('REACT_APP_BLOB_STORAGE_URL not configured');
          const azureFont = new FontFace(
            'AED-Regular',
            `url(${azureFontUrl})`
          );
          await azureFont.load();
          document.fonts.add(azureFont);
          fontLoaded = true;
          console.log('✅ AED-Regular font loaded from Azure');
        } catch (azureError) {
          console.log('❌ Azure font failed:', azureError.message);
          console.log('🔄 Trying bundled local font...');

          try {
            // 2. Fallback to bundled local font
            const localFont = new FontFace('AED-Regular', `url(${aedRegularFont})`);
            await localFont.load();
            document.fonts.add(localFont);
            fontLoaded = true;
            console.log('✅ AED-Regular font loaded from bundle');
          } catch (localError) {
            console.log('❌ Local font failed:', localError);
            throw new Error('Both Azure and local font loading failed');
          }
        }

        setFontLoaded(fontLoaded);
      } else {
        setFontError('FontFace API not supported');
        console.log('❌ FontFace API not supported');
      }
    } catch (error) {
      setFontError(`Font loading failed: ${error.message}`);
      console.log('❌ Font loading failed:', error);
      setFontLoaded(false);
    }
  };

  checkFont();
}, []);


  React.useEffect(() => {
    if (containerRef.current) {
      // Get the rendered HTML from ReactMarkdown
      const htmlContent = containerRef.current.innerHTML;
      
      // Replace ALL instances of AED with Dirham symbol or fallback
      const replacementSpan = fontLoaded 
        ? '<span class="dirham-symbol" style="font-family: \'AED-Regular\', Arial, sans-serif !important;">D</span>'
        : '<span class="dirham-symbol-fallback" style="font-family: Arial, sans-serif !important;">AED</span>';
      console.log('HTML content:', htmlContent);
      let processedContent = htmlContent.replace(/AED/g, replacementSpan);
      processedContent = processedContent.replace(/درهم/g, replacementSpan);
      processedContent = processedContent.replace(/دراهم/g, replacementSpan);
      // replace the • with <br> •
      processedContent = processedContent.replace(/•/g, '<br>•');
      setProcessedHTML(processedContent);
    }
  }, [children, fontLoaded, fontError]);

  return (
    <div>
      {/* Hidden ReactMarkdown to get processed HTML */}
      <div ref={containerRef} style={{ display: 'none' }}>
        <ReactMarkdown {...markdownProps}>
          {fixPhoneNumbersRTL(children)}
        </ReactMarkdown>
      </div>
      
      {/* Display processed content with AED replacement */}
      <div dangerouslySetInnerHTML={{ __html: processedHTML }} />
    </div>
  );
};

const MessageItem = ({
  activity,
  sources,
  id,
  responseGood,
  suggestedActionlist,
  onSuggestionClick,
  isArabic,
  surveyData,
  redeemData,
  disableSend,
  locked,
  offerData,
  pendingActionCards,
  paymentReceipt,
  offerDetail,
  triggeredby,
  previousMessageHasArabic = false, // Add this prop to track previous message
  basicLogoUrl // Add basicLogoUrl prop
}) => {
  const arabicRegex = /[\u0600-\u06FF]/; // Arabic character range
  const numericRegex = /^-?\d+(\.\d+)?$/; // Matches a string with only numbers

  const [script, setScript] = useState("");
  const [copied, setCopied] = useState(false);

  const handleActionClick = (actionType) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 5000);
  };

  const isTyping = activity.type === "typing";
  console.log('activity' , activity)
  console.log('activity text' , activity.text)
  const text = isTyping
    ? activity.text
    : getValueOrUndefined(activity, "channelData", "messageBack", "displayText") || activity.text;

  const isUser = activity.from.role === "user";
  const isAgent = activity.channelData.role === "agent";

  // Check if the message contains Arabic characters
  const hasArabicContent = text && arabicRegex.test(text);

  // For messages with no text, follow the alignment of the previous message or general Arabic setting
  const shouldAlignRight = hasArabicContent || isArabic || (!text && (previousMessageHasArabic || isArabic));

  const getLocal = (text) => {
    if (isArabic && numericRegex.test(text)) {
      return "ar-EG";
    }
    if (isArabic && !text) {
      return "ar-EG";
    }
    return arabicRegex.test(text) ? "ar-EG" : "en-GB";
  };

  const isArabicText = (text) => {
    if (isArabic && typeof text === "string" && text.split("|||").length > 1) {
      const parts = text.split("|||");
      return arabicRegex.test(parts[1] ?? "");
    }
    return arabicRegex.test(text?.split("|||")[0] ?? "");
  };

  const convertArabicToEnglishDigits = (str) => {
    const arabicDigits = "٠١٢٣٤٥٦٧٨٩"; // Arabic digits
    const englishDigits = "0123456789"; // English digits
    return str
      .split("")
      .map((char) => {
        const index = arabicDigits.indexOf(char);
        return index !== -1 ? englishDigits[index] : char;
      })
      .join("");
  };

  const timestamp = activity.timestamp ? (
    new Date(activity.timestamp)
      .toLocaleDateString(getLocal(text), {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .split("")
      .map((char) => convertArabicToEnglishDigits(char))
      .join("") +
    " " +
    new Date(activity.timestamp)
      .toLocaleTimeString(getLocal(text), {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
      .split("")
      .map((char) => convertArabicToEnglishDigits(char))
      .join("")
  ) : "";  // Return empty string if timestamp is not valid

  const messageStyles = isUser
    ? "bg-[#e0eeec] shadow-lg" // User message style
    : isAgent
    ? "bg-blue-100" // Agent message style
    : "bg-red "; // Bot message style

  const avatarSrc = isAgent ? agentAvatar : (basicLogoUrl || logo); // Set avatar based on the sender

  const getDirection = (text) => {
    if (isArabic && !text) {
      return "rtl";
    }
    if (isArabic && numericRegex.test(text)) {
      return "rtl";
    }
    if (!text && (previousMessageHasArabic || isArabic)) {
      return "rtl";
    }
    return arabicRegex.test(text) ? "rtl" : "ltr";
  };

  const getTextAlign = (text) => {
    if (isArabic || hasArabicContent) {
      return "right";
    }
    if (!text && (previousMessageHasArabic || isArabic)) {
      return "right";
    }
    return arabicRegex.test(text) ? "right" : "left";
  };

  const cardChosen = activity?.attachments?.length > 0
    ? isArabic && activity?.attachments?.length > 1
      ? activity?.attachments[1]
      : activity?.attachments[0]
    : null;

  const isMobile = window.innerWidth <= 1000; // Check if the screen size is mobile
  const fontSize = isMobile ? "0.8rem" : "16px"; // Adjust as needed

  const markdownProps = {
    className: "break-words",
    remarkPlugins: [remarkGfm],
    components: {
      a: ({ node, children, ...props }) => (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#007560",
            textDecoration: "underline",
          }}
        >
          {children || props.href}
        </a>
      ),
      // Removed the li component - let ReactMarkdown handle lists naturally
    }
  };

  return (
    <>
      {/* Add font styles */}
      <style>{fontStyles}</style>
      
      <li
        className={`flex flex-col ${
          isUser ? "items-end" : shouldAlignRight ? "items-end" : "items-start"
        } p-0 m-0`}
        style={{
          textAlign: getTextAlign(text),
          direction: getDirection(text),
        }}
      >
        <div
          className={`flex ${isUser ? "justify-end" : shouldAlignRight ? "justify-end" : "justify-start"} w-full`}
        >
          {isUser ? (
            <div className="flex flex-col items-end">
              <div
                className={`inline-block max-w-max md:max-w-lg w-auto p-2 ${messageStyles} ml-12 text-right rounded-lg`}
                style={{
                  background: isTyping ? "rgba(0, 117, 96, 0.12)" : undefined,
                  textAlign: getTextAlign(text),
                  direction: getDirection(text),
                }}
              >
                {isTyping ? (
                  <div className="flex items-center">
                    <div className="w-6 h-6 rounded-full bg-gray-300 animate-pulse mr-2"></div>
                    <span className="text-gray-600">{text}</span>
                  </div>
                ) : (
                  text && (
                    <MarkdownWithAEDReplacement {...markdownProps}>
                      {text}
                    </MarkdownWithAEDReplacement>
                  )
                )}
                {activity?.attachments?.length > 0 && (
                  <ul className="mt-2 space-y-2">
                    {activity?.attachments?.map((attachment, index) => (
                      <li key={index}>
                        <Attachment {...attachment} isArabic={isArabic} />
                      </li>
                    ))}
                  </ul>
                )}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={() => handleActionClick("copy")}
                        className="text-gray-500 p-1"
                      >
                        {copied ? (
                          <FaCheck className="w-4 h-5" />
                        ) : (
                          <FaRegCopy className="w-3 h-4" />
                        )}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>{copied ? "Copied!" : "Copy"}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {/* Ensure it starts from the right */}
              <div
                className={`flex items-center w-full mt-1 ${
                  shouldAlignRight
                    ? "flex-row-reverse justify-end"
                    : "flex-row justify-end"
                }`}
              >
                <span
                  className="text-gray-500 text-sm"
                  style={{
                    marginLeft: shouldAlignRight ? "8px" : "0",
                    marginRight: shouldAlignRight ? "0" : "8px",
                    fontSize,
                    display: timestamp ? 'inline' : 'none'  // Hide if no timestamp
                  }}
                >
                  {timestamp}
                </span>
                <Avatar className="flex items-center">
                  <AvatarImage
                    className="w-[25px] h-[25px] rounded-full object-cover opacity-75"
                    src={PersonIcon}
                  />
                </Avatar>
              </div>
            </div>
          ) : (
            <div className={`flex ${shouldAlignRight ? "flex-row-reverse" : "flex-row"} items-start w-full`}>
              <div className={`flex items-center flex-row ${shouldAlignRight ? "order-last" : "order-first"}`}>
                {isAgent ? (
                  <BiSupport className="w-[25px] h-[25px] rounded-full object-cover" />
                ) : (
                  <Avatar className="flex items-center">
                    <AvatarImage
                      className="w-[25px] h-[25px] rounded-full object-cover"
                      src={avatarSrc}
                      alt={isAgent ? "Agent Avatar" : "Bot Avatar"}
                    />
                    <AvatarFallback className="absolute">
                      {isAgent ? "A" : "R"}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
              <div className={`flex-1 ${shouldAlignRight ? "mr-2" : "ml-2"}`}>
                <div
                  className={`${messageStyles} rounded-lg w-full`}
                  style={{
                    padding: '8px',
                    width: '100%',
                    maxWidth: '100%',
                    display: 'block',
                    textAlign: getTextAlign(text),
                    direction: getDirection(text),
                  }}
                >
                  {text === "Bot is thinking..." || text === "البوت يفكر..." ? (
                    <BotThinking visible={true} isArabic={isArabic} />
                  ) : (
                    <div>
                      {isTyping ? (
                        <div
                          className="flex items-center"
                          dir={getDirection(text)}
                          style={{ textAlign: getTextAlign(text) }}
                        >
                          <ReactMarkdown
                            className="break-words"
                            remarkPlugins={[remarkGfm]}
                            components={{
                              a: ({ node, children, ...props }) => (
                                <a
                                  {...props}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#007560",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {children || props.href}
                                </a>
                              ),
                            }}
                          >
                            {text}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <>
                          {text && (
                            <div style={{ textAlign: getTextAlign(text), direction: getDirection(text) }}>
                              <MarkdownWithAEDReplacement {...markdownProps}>
                                {text}
                              </MarkdownWithAEDReplacement>
                            </div>
                          )}

                          {cardChosen && (
                            <Attachment {...cardChosen} disableSend={disableSend} setScript={setScript} isArabic={shouldAlignRight} id={id} surveyData={surveyData} redeemData={redeemData} triggeredby={triggeredby} />
                          )}

                          {pendingActionCards && (
                            <PendingActionCards
                              data={pendingActionCards}
                              isArabic={shouldAlignRight}
                              locked={locked}
                            />
                          )}

                          {paymentReceipt && (
                            <PaymentReceiptCard
                              data={paymentReceipt}
                              isArabic={shouldAlignRight}
                            />
                          )}

                          {offerData && (
                            <div style={{
                              width: '100%',
                              maxWidth: '100%',
                              margin: '0',
                              padding: '0',
                              display: 'block',
                              textAlign: getTextAlign(text),
                              direction: getDirection(text),
                            }}>
                              <Offers offers={offerData} isArabic={shouldAlignRight} locked={locked} />
                            </div>
                          )}

                          {offerDetail && (
                            <div className="mt-4" style={{
                              textAlign: getTextAlign(text),
                              direction: getDirection(text),
                            }}>
                              <OfferDetail offer={offerDetail} isArabic={shouldAlignRight} locked={locked} />
                            </div>
                          )}

                          <span
                            className="text-gray-500"
                            style={{ 
                              fontSize,
                              display: timestamp ? 'inline' : 'none'
                            }}
                          >
                            {timestamp}
                          </span>
                          {!isAgent && (
                            <ActionButtons
                              activity={activity}
                              text={text}
                              id={id}
                              responseGood={responseGood}
                              script={script}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {sources && sources?.length > 0 && (
          <div className={`mt-2  ${isArabic ? "mr-8" : "ml-8"}`}>
            <SourceList sources={sources} isArabic={isArabic} />
          </div>
        )}
    <div className="w-full flex">
  <div className={`${shouldAlignRight ? "mr-11" : "ml-11"}`}>
    <SuggestionActionList
      suggestions={suggestedActionlist}
      onSuggestionClick={onSuggestionClick}
      isArabic={shouldAlignRight}
      locked={locked}
    />
  </div>
</div>


      </li>
    </>
  );
};

export default MessageItem;
